import { useLogger } from "@amzn/xb-hooks";
import { Helmet } from "react-helmet";
import { LiveInterceptorFactory } from "../live-component";

/**
 * Metadata tags in the document head, primarily for SEO and social sharing. Handles both basic HTML
 * meta tags and specific tags for OpenGraph and Twitter sharing.
 *
 * @property {string} [title] - The page title, used in browser tab and social shares
 * @property {string} [description] - Brief description of the page content
 * @property {string} [image] - URL of the image to be displayed in social share cards
 * @property {string} [canonicalUrl] - The canonical URL for this page
 * @property {string} [type] - Content type (e.g., 'website', 'article') for OpenGraph
 * @property {string} [twitterCard] - Twitter card type (e.g., 'summary', 'summary_large_image')
 *
 * @example
 * {
 *   metaData: {
 *     title: "Product Page",
 *     description: "Description of the product",
 *     image: "https://example.com/image.jpg",
 *     canonicalUrl: "https://example.com/product",
 *     type: "website",
 *     twitterCard: "summary_large_image"
 *   }
 * }
 */
export type MetaDataInterface = {
  metaData?: {
    title?: string;
    description?: string;
    image?: string;
    canonicalUrl?: string;
    type?: string;
    twitterCard?: string;
  };
};

export const metaDataInterceptorFactory: LiveInterceptorFactory<MetaDataInterface> = (_, Response) => {
  const ResponseWithMetadata = (props: MetaDataInterface) => {
    const { debug } = useLogger();
    const { metaData: { title, description, image, canonicalUrl, type, twitterCard } = {} } = props;
    debug(`adding meta data ${JSON.stringify(props)}`);

    return (
      <>
        <Helmet>
          {/* Basic tags */}
          {title && <title>{title}</title>}
          {description && <meta name="description" content={description} />}

          {/* OpenGraph tags - Twitter will use image, title, and description from OpenGraph, so we don't need to define them
          for each - unless we want different tags when sharing on Twitter. https://tiny.amazon.com/hm2tcc4r/twitterTags */}
          {title && <meta property="og:title" content={title} />}
          {description && <meta property="og:description" content={description} />}
          {image && <meta property="og:image" content={image} />}
          {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
          {type && <meta property="og:type" content={type} />}

          {/* Twitter card types - https://tiny.amazon.com/2zgfvztl/twitterCardTypes */}
          {twitterCard && <meta name="twitter:card" content={twitterCard} />}
        </Helmet>
        <Response {...props} />
      </>
    );
  };
  return ResponseWithMetadata;
};
